import moment from "moment";
import PropTypes from "prop-types";
import { path, find, propEq, groupBy, uniq } from "ramda";
import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  ButtonToolbar,
  Col,
  Form,
  Row,
  DropdownButton,
  Dropdown,
  InputGroup,
  ListGroup,
  ButtonGroup,
} from "react-bootstrap";
import DatePicker from "react-datepicker";
import { FormattedMessage, useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import AsyncSelect from "react-select/async";

import DriveSuggest, { LABELS } from "../../../../components/DriveSuggest.jsx";
import UploadBase64 from "../../../../components/imageUploader/ImageUploader.jsx";
import genericTranslations from "../../../../utils/genericTranslations.js";
import { selectContentsByBroadcastId, selectFolders } from "../../selectors.js";
import { fetchContentItemsFiltered } from "../../thunks.js";

const styles = {
  thumbnail: {
    marginBottom: 10,
    width: "100%",
    height: "auto",
  },
  userList: {
    marginBottom: 10,
  },
};
const messages = {
  title: {
    id: "modules.ArriVideo.components.BroadcastForm.title",
    defaultMessage: "Title",
  },
  fromDateTime: {
    id: "modules.ArriVideo.components.BroadcastForm.from",
    defaultMessage: "From",
  },
  noAudiences: {
    id: "modules.ArriVideo.components.BroadcastForm.noAudiences",
    description: "Broadcast no audiences label.",
    defaultMessage: "No groups or users paired",
  },
  toDateTime: {
    id: "modules.ArriVideo.components.BroadcastForm.to",
    defaultMessage: "To",
  },
  timeCaption: {
    id: "modules.ArriVideo.components.BroadcastForm.timeCaption",
    defaultMessage: "Time",
  },
  todayButton: {
    id: "modules.ArriVideo.components.BroadcastForm.todayButton",
    defaultMessage: "Today",
  },
  upload: {
    id: "modules.ArriVideo.components.BroadcastForm.uploadButtonText",
    defaultMessage: "Upload image",
  },
  action: {
    id: "modules.ArriVideo.components.BroadcastForm.action",
    defaultMessage: "Action",
  },
  actionTypeCall: {
    id: "modules.ArriVideo.components.BroadcastForm.actionTypeCall",
    defaultMessage: "Call",
  },
  actionTypeMail: {
    id: "modules.ArriVideo.components.BroadcastForm.actionTypeMail",
    defaultMessage: "Mail",
  },
  actionTypeNone: {
    id: "modules.ArriVideo.components.BroadcastForm.actionTypeNone",
    defaultMessage: "No action",
  },
  actionTypeSite: {
    id: "modules.ArriVideo.components.BroadcastForm.actionTypeSite",
    defaultMessage: "Website",
  },
  audiences: {
    id: "modules.ArriVideo.components.BroadcastForm.audiences",
    defaultMessage: "Audiences",
  },
  contentItems: {
    id: "modules.ArriVideo.components.BroadcastForm.contentItems",
    defaultMessage: "Content items",
  },
  contentItemsSelectPlaceholder: {
    id: "modules.ArriVideo.components.BroadcastForm.contentItemsSelectPlaceholder",
    defaultMessage: "Search here for content items...",
  },
  notification: {
    id: "modules.ArriVideo.components.BroadcastForm.notification",
    defaultMessage: "Notification",
  },
  notificationSend: {
    id: "modules.ArriVideo.components.BroadcastForm.notificationSend",
    defaultMessage: "Send a notification when the broadcast is published",
  },
  notificationText: {
    id: "modules.ArriVideo.components.BroadcastForm.notificationText",
    defaultMessage: "Notification text",
  },
  notificationTextHelp: {
    id: "modules.ArriVideo.components.BroadcastForm.notificationTextHelp",
    description: "Broadcast notification text help label.",
    defaultMessage: `Default notification text will only the title of the broadcast.".
Override the default notification text the user will receive.
Use '{'title'}' to automatically insert the broadcast title.`,
  },
  folder: {
    id: "modules.ArriVideo.components.BroadcastForm.folder",
    defaultMessage: "Folder",
  },
  noFolder: {
    id: "modules.ArriVideo.components.BroadcastForm.noFolder",
    defaultMessage: "No folder",
  },
  thumbnail: {
    id: "modules.ArriVideo.components.BroadcastForm.thumbnail",
    description: "Broadcast thumbnail label.",
    defaultMessage: "Thumbnail",
  },
};

const BroadcastForm = ({
  handleChange,
  handleSubmit,
  errors,
  isSubmitting,
  onClickDelete,
  setFieldValue,
  values,
}) => {
  const intl = useIntl();
  const folders = useSelector(selectFolders);
  const contentItems = useSelector((state) =>
    selectContentsByBroadcastId(state, { value: values.id }),
  );
  const dispatch = useDispatch();

  // Local state for audiences and ordered content items
  const [audiences, setAudiences] = useState(values.audiences || []);
  const [contentItemsOrdered, setContentItemsOrdered] = useState(
    contentItems || [],
  );

  useEffect(() => {
    setFieldValue("audiences", audiences);
    if (contentItemsOrdered) {
      setFieldValue(
        "contentItems",
        contentItemsOrdered.map((c, idx) => ({ id: c.id, order: idx })),
      );
    }
  }, [audiences, contentItemsOrdered, setFieldValue]);

  const handleSorting = useCallback(
    (direction, index) => {
      const ordered = [...contentItemsOrdered];
      const tmp = ordered[index];
      if (direction === "up" && index > 0) {
        ordered[index] = ordered[index - 1];
        ordered[index - 1] = tmp;
      } else if (direction === "down" && index < ordered.length - 1) {
        ordered[index] = ordered[index + 1];
        ordered[index + 1] = tmp;
      }
      setContentItemsOrdered(ordered);
    },
    [contentItemsOrdered],
  );

  const groupedAudiences = groupBy(
    (a) => LABELS[path(["value", "resourceType"], a)] || "Onbekend",
  )(audiences);

  const loadSelectOptions = async (query) => {
    let contentItems;
    try {
      contentItems = await dispatch(
        fetchContentItemsFiltered({ searchTitle: query }),
      );
    } catch (err) {
      console.error("Error fetching the select options", err);
    }

    return contentItems.map((c) => ({
      label: c.title,
      value: c.id,
      isDisabled: !!find(propEq(c.id, "id"))(contentItemsOrdered),
    }));
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Row>
        <Col xs={12} md={8} lg={8}>
          <Form.Group controlId="title" className="mb-3">
            <Form.Label>
              <FormattedMessage {...messages.title} />
            </Form.Label>
            <Form.Control
              type="text"
              autoComplete="off"
              value={values.title || ""}
              onChange={handleChange}
            />
            {errors.title && (
              <Form.Text className="text-danger">{errors.title}</Form.Text>
            )}
          </Form.Group>

          <Form.Group controlId="notification" className="mb-3">
            <Form.Check
              type="checkbox"
              label={<FormattedMessage {...messages.notificationSend} />}
              checked={values.notification || false}
              onChange={(e) => setFieldValue("notification", e.target.checked)}
            />
            {errors.notification && (
              <Form.Text className="text-danger">
                {errors.notification}
              </Form.Text>
            )}
          </Form.Group>

          <Form.Group controlId="notificationText" className="mb-3">
            <Form.Label>
              <FormattedMessage {...messages.notificationText} />
            </Form.Label>
            <Form.Control
              type="text"
              autoComplete="off"
              value={values.notificationText || ""}
              onChange={handleChange}
              placeholder={values.title}
            />
            <Form.Text>
              <span style={{ whiteSpace: "pre-wrap" }}>
                <FormattedMessage {...messages.notificationTextHelp} />
              </span>
            </Form.Text>
            {errors.notificationText && (
              <Form.Text className="text-danger">
                {errors.notificationText}
              </Form.Text>
            )}
          </Form.Group>

          <Form.Group controlId="action" className="mb-3">
            <Form.Label>
              <FormattedMessage {...messages.action} />
            </Form.Label>
            <InputGroup>
              <DropdownButton
                id="actionDropdown"
                as={ButtonGroup}
                variant="secondary"
                title={
                  values.actionType
                    ? intl.formatMessage(
                        messages[
                          `actionType${values.actionType.charAt(0).toUpperCase() + values.actionType.slice(1)}`
                        ],
                      )
                    : intl.formatMessage(messages.action)
                }
                onSelect={(type) => {
                  setFieldValue("actionType", type);
                  if (!type) {
                    setFieldValue("actionContent", "");
                  }
                }}
              >
                {["call", "mail", "site"].map((type) => (
                  <Dropdown.Item key={`action-type-${type}`} eventKey={type}>
                    {intl.formatMessage(
                      messages[
                        `actionType${type.charAt(0).toUpperCase() + type.slice(1)}`
                      ],
                    )}
                  </Dropdown.Item>
                ))}
                <Dropdown.Divider />
                <Dropdown.Item key="action-type-none" eventKey="">
                  {intl.formatMessage(messages.actionTypeNone)}
                </Dropdown.Item>
              </DropdownButton>

              <Form.Control
                disabled={!values.actionType}
                type="text"
                autoComplete="off"
                value={values.actionContent || ""}
                onChange={(e) => setFieldValue("actionContent", e.target.value)}
              />
            </InputGroup>
            {errors.action && (
              <Form.Text className="text-danger">{errors.action}</Form.Text>
            )}
          </Form.Group>

          <Form.Group controlId="contentItems" className="mb-3">
            <Form.Label>
              <FormattedMessage {...messages.contentItems} />
            </Form.Label>
            <div
              style={{
                backgroundColor: "#E9EAE9",
                padding: 20,
                marginBottom: 0,
              }}
            >
              <AsyncSelect
                placeholder={intl.formatMessage(
                  messages.contentItemsSelectPlaceholder,
                )}
                name="content-items-select"
                loadOptions={loadSelectOptions}
                onChange={(selectedOption) => {
                  const newList = [
                    ...contentItemsOrdered,
                    {
                      id: selectedOption.value,
                      title: selectedOption.label,
                      order: contentItemsOrdered.length,
                    },
                  ];
                  setContentItemsOrdered(newList);
                }}
              />
              <br />
              {contentItemsOrdered && contentItemsOrdered.length > 0 && (
                <ListGroup>
                  {contentItemsOrdered.map((item, idx) => (
                    <ListGroup.Item
                      key={`content-item-${item.id}`}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      {item.title}
                      <ButtonToolbar>
                        <Button
                          size="sm"
                          className="me-2"
                          variant="light"
                          disabled={idx === 0}
                          onClick={() => handleSorting("up", idx)}
                        >
                          &#9650;
                        </Button>
                        <Button
                          size="sm"
                          className="me-2"
                          variant="light"
                          disabled={idx === contentItemsOrdered.length - 1}
                          onClick={() => handleSorting("down", idx)}
                        >
                          &#9660;
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() =>
                            setContentItemsOrdered(
                              contentItemsOrdered.filter((_, i) => i !== idx),
                            )
                          }
                        >
                          <FormattedMessage {...genericTranslations.delete} />
                        </Button>
                      </ButtonToolbar>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </div>
            {errors.contentItems && (
              <Form.Text className="text-danger">
                {errors.contentItems}
              </Form.Text>
            )}
          </Form.Group>

          <Form.Group controlId="audiences" className="mb-3">
            <Form.Label>
              <FormattedMessage {...messages.audiences} />
            </Form.Label>
            <div
              style={{
                backgroundColor: "#E9EAE9",
                padding: 20,
                marginBottom: 0,
              }}
            >
              <DriveSuggest
                onSelect={(audience) => {
                  if (
                    !find(
                      (a) =>
                        path(["value", "id"], a) ===
                        path(["value", "id"], audience),
                    )(audiences)
                  ) {
                    const newAudiences = [...audiences, audience];
                    setFieldValue("audiences", newAudiences);
                    setAudiences(newAudiences);
                  }
                }}
              />
              <hr />
              {(!audiences || audiences.length === 0) && (
                <span>
                  <FormattedMessage {...messages.noAudiences} />
                </span>
              )}
              {audiences && audiences.length > 0 && (
                <div>
                  {uniq(Object.values(LABELS)).map((resourceType) => {
                    if (!groupedAudiences[resourceType]) return null;
                    return (
                      <div key={resourceType}>
                        <span>{resourceType}</span>
                        <ListGroup style={styles.userList}>
                          {groupedAudiences[resourceType].map((audience) => (
                            <ListGroup.Item
                              key={`audience-${audience.value.id}`}
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              {audience.label}
                              <Button
                                size="sm"
                                variant="danger"
                                onClick={() =>
                                  setAudiences(
                                    audiences.filter(
                                      (a) =>
                                        path(["value", "id"], a) !==
                                        path(["value", "id"], audience),
                                    ),
                                  )
                                }
                              >
                                <FormattedMessage
                                  {...genericTranslations.delete}
                                />
                              </Button>
                            </ListGroup.Item>
                          ))}
                        </ListGroup>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            {errors.audiences && (
              <Form.Text className="text-danger">{errors.audiences}</Form.Text>
            )}
          </Form.Group>

          <hr />

          <Form.Group controlId="formActions" className="mb-3">
            <ButtonToolbar>
              {values.id && (
                <Button
                  variant="danger"
                  className="me-2"
                  onClick={() => onClickDelete()}
                >
                  <FormattedMessage {...genericTranslations.delete} />
                </Button>
              )}
              <Button
                type="submit"
                disabled={isSubmitting || Object.keys(errors).length > 0}
              >
                <FormattedMessage {...genericTranslations.formSubmitLabel} />
              </Button>
            </ButtonToolbar>
          </Form.Group>
        </Col>

        <Col xs={12} md={3} lg={3} className="offset-md-1 offset-lg-1">
          <Form.Group controlId="fromDateTime" className="mb-3">
            <Form.Label>
              <FormattedMessage {...messages.fromDateTime} />
            </Form.Label>
            <br />
            <DatePicker
              className="form-control"
              selected={moment(values.from).toDate()}
              onChange={(dateTime) => setFieldValue("from", dateTime)}
              showTimeSelect
              dateFormat="MMMM d, yyyy HH:mm"
              timeFormat="HH:mm"
              timeCaption={intl.formatMessage(messages.timeCaption)}
              timeIntervals={15}
              todayButton={intl.formatMessage(messages.todayButton)}
            />
            {errors.from && (
              <Form.Text className="text-danger">{errors.from}</Form.Text>
            )}
          </Form.Group>

          <Form.Group controlId="toDateTime" className="mb-3">
            <Form.Label>
              <FormattedMessage {...messages.toDateTime} />
            </Form.Label>
            <br />
            <DatePicker
              className="form-control"
              selected={moment(values.to).toDate()}
              onChange={(dateTime) => setFieldValue("to", dateTime)}
              showTimeSelect
              dateFormat="MMMM d, yyyy HH:mm"
              timeFormat="HH:mm"
              timeCaption={intl.formatMessage(messages.timeCaption)}
              timeIntervals={15}
              todayButton={intl.formatMessage(messages.todayButton)}
            />
            {errors.to && (
              <Form.Text className="text-danger">{errors.to}</Form.Text>
            )}
          </Form.Group>

          <Form.Group controlId="folder" className="mb-3">
            <Form.Label>
              <FormattedMessage {...messages.folder} />
            </Form.Label>
            <DropdownButton
              id="folderDropdown"
              variant="secondary"
              title={
                values.folder?.title || intl.formatMessage(messages.noFolder)
              }
              onSelect={(eventKey) => {
                if (eventKey === "-1") {
                  // Set to "No Folder"
                  setFieldValue("folder", {
                    id: "-1",
                    title: intl.formatMessage(messages.noFolder),
                  });
                } else {
                  const selectedFolder = folders.find(
                    (folder) => folder.id === eventKey,
                  );
                  if (selectedFolder) {
                    setFieldValue("folder", selectedFolder);
                  }
                }
              }}
            >
              {folders.map((folder) => (
                <Dropdown.Item key={folder.id} eventKey={folder.id}>
                  {folder.title}
                </Dropdown.Item>
              ))}
              <Dropdown.Divider />
              <Dropdown.Item key={-1} eventKey={-1}>
                {intl.formatMessage(messages.noFolder)}
              </Dropdown.Item>
            </DropdownButton>
            {errors.folder && (
              <Form.Text className="text-danger">{errors.folder}</Form.Text>
            )}
          </Form.Group>

          <Form.Group controlId="thumbnail" className="mb-3">
            <Form.Label>
              <FormattedMessage {...messages.thumbnail} />
            </Form.Label>
            <div style={{ width: 250 }}>
              {values.thumbnail && (
                <img
                  alt="thumbnail"
                  src={`data:image/png;base64,${values.thumbnail}`}
                  height={256}
                  style={styles.thumbnail}
                />
              )}
              <UploadBase64
                useCropper
                removeBase64DataUriPrefix
                buttonText={intl.formatMessage(messages.upload)}
                cropperProps={{
                  aspectRatio: 16 / 9,
                  scale: [455, 256],
                }}
                onFileReady={(data) => setFieldValue("thumbnail", data)}
              />
            </div>
            {errors.thumbnail && (
              <Form.Text className="text-danger">{errors.thumbnail}</Form.Text>
            )}
          </Form.Group>
        </Col>
      </Row>
    </Form>
  );
};

BroadcastForm.propTypes = {
  handleChange: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  onClickDelete: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  errors: PropTypes.object,
  values: PropTypes.object.isRequired,
};

export default BroadcastForm;
